import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bot, ChevronRight, Pause, Play, Plus, Pencil, Trash2 } from "lucide-react";
import { PageHeader, Panel } from "@/components/dashboard/primitives";
import { StatusBadge, RiskBadge, fmtUSD } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import Loading from "@/components/dashboard/Loading";
import { useDashboard, useDashboardActions } from "@/hooks/useDashboard";
import { toast } from "sonner";

const EMPTY = { name: "", monthlyBudget: 500, dailyLimit: 100, transactionLimit: 100, risk: "Low", status: "Active" };

export default function Agents() {
  const navigate = useNavigate();
  const { data, isLoading } = useDashboard();
  const { setAgentStatus, createAgent, updateAgent, deleteAgent } = useDashboardActions();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  if (isLoading || !data) {
    return (<div><PageHeader title="Agents" subtitle="Autonomous agents connected to your Vvvoraa workspace." /><Loading /></div>);
  }

  const toggle = async (agent, e) => {
    e.stopPropagation();
    const next = agent.status === "Active" ? "Paused" : "Active";
    await setAgentStatus(agent.id, next);
    toast.success(`${agent.name} ${next === "Active" ? "resumed" : "paused"}`);
  };

  const openCreate = () => { setEditing(null); setForm(EMPTY); setDialogOpen(true); };
  const openEdit = (a, e) => {
    e.stopPropagation();
    setEditing(a);
    setForm({ name: a.name, monthlyBudget: a.monthlyBudget, dailyLimit: a.dailyLimit, transactionLimit: a.transactionLimit, risk: a.risk, status: a.status });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Agent name is required"); return; }
    setSaving(true);
    const body = {
      name: form.name.trim(),
      monthlyBudget: Number(form.monthlyBudget) || 0,
      dailyLimit: Number(form.dailyLimit) || 0,
      transactionLimit: Number(form.transactionLimit) || 0,
      risk: form.risk,
      status: form.status,
    };
    try {
      if (editing) { await updateAgent(editing.id, body); toast.success("Agent updated"); }
      else { await createAgent(body); toast.success("Agent created"); }
      setDialogOpen(false);
    } catch { toast.error("Could not save agent"); }
    finally { setSaving(false); }
  };

  const confirmDelete = async () => {
    try { await deleteAgent(toDelete.id); toast.success(`${toDelete.name} deleted`); }
    catch { toast.error("Could not delete agent"); }
    finally { setToDelete(null); }
  };

  return (
    <div>
      <PageHeader title="Agents" subtitle="Autonomous agents connected to your Vvvoraa workspace.">
        <Button onClick={openCreate} className="bg-[#4F8CFF] hover:bg-[#3f7af0] text-white rounded-full gap-1.5" data-testid="add-agent-btn">
          <Plus className="h-4 w-4" /> New agent
        </Button>
      </PageHeader>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {data.agents.map((a) => {
          const pct = Math.min((a.spent / a.monthlyBudget) * 100, 100);
          return (
            <Panel key={a.id} className="p-5 vora-card-hover cursor-pointer" onClick={() => navigate(`/app/agents/${a.id}`)} data-testid={`agent-card-${a.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#4F8CFF]/12 text-[#4F8CFF]">
                    <Bot className="h-5 w-5" />
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-white">{a.name}</p>
                    <div className="flex items-center gap-1.5 mt-1">
                      <StatusBadge status={a.status} />
                      <RiskBadge risk={a.risk} />
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={(e) => openEdit(a, e)} className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/5 transition-colors" data-testid={`agent-edit-${a.id}`}><Pencil className="h-3.5 w-3.5" /></button>
                  <button onClick={(e) => { e.stopPropagation(); setToDelete(a); }} className="p-1.5 rounded-lg text-slate-400 hover:text-red-400 hover:bg-red-500/10 transition-colors" data-testid={`agent-delete-${a.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                  <ChevronRight className="h-4 w-4 text-slate-500" />
                </div>
              </div>

              <div className="mt-5 flex items-center justify-between text-xs text-slate-400">
                <span>Spent <span className="text-white">{fmtUSD(a.spent)}</span></span>
                <span>Budget <span className="text-white">{fmtUSD(a.monthlyBudget)}</span></span>
              </div>
              <div className="mt-2 h-1.5 rounded-full bg-white/8 overflow-hidden">
                <div className="h-full rounded-full bg-gradient-to-r from-[#4F8CFF] to-[#7C5CFF]" style={{ width: `${pct}%` }} />
              </div>

              <div className="mt-4 flex items-center justify-between">
                <span className="text-xs text-slate-500">Wallet {fmtUSD(a.walletBalance)}</span>
                <Button size="sm" variant="outline" onClick={(e) => toggle(a, e)} className="h-8 bg-transparent border-white/12 text-white hover:bg-white/5 rounded-full gap-1.5" data-testid={`agent-toggle-${a.id}`}>
                  {a.status === "Active" ? <><Pause className="h-3.5 w-3.5" /> Pause</> : <><Play className="h-3.5 w-3.5" /> Resume</>}
                </Button>
              </div>
            </Panel>
          );
        })}
      </div>

      {/* Create / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#0A1119] border-white/10 text-white" data-testid="agent-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit agent" : "New agent"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label className="text-slate-300">Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Sales Agent" className="bg-white/[0.03] border-white/12 text-white" data-testid="agent-form-name" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label className="text-slate-300 text-xs">Monthly budget ($)</Label>
                <Input type="number" value={form.monthlyBudget} onChange={(e) => setForm({ ...form, monthlyBudget: e.target.value })} className="bg-white/[0.03] border-white/12 text-white" data-testid="agent-form-budget" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-slate-300 text-xs">Daily limit ($)</Label>
                <Input type="number" value={form.dailyLimit} onChange={(e) => setForm({ ...form, dailyLimit: e.target.value })} className="bg-white/[0.03] border-white/12 text-white" data-testid="agent-form-daily" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-slate-300 text-xs">Per-txn limit ($)</Label>
                <Input type="number" value={form.transactionLimit} onChange={(e) => setForm({ ...form, transactionLimit: e.target.value })} className="bg-white/[0.03] border-white/12 text-white" data-testid="agent-form-txn" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-slate-300 text-xs">Risk</Label>
                <Select value={form.risk} onValueChange={(v) => setForm({ ...form, risk: v })}>
                  <SelectTrigger className="bg-white/[0.03] border-white/12 text-white" data-testid="agent-form-risk"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#0A1119] border-white/10 text-white">
                    {["Low", "Medium", "High"].map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-slate-300 text-xs">Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger className="bg-white/[0.03] border-white/12 text-white" data-testid="agent-form-status"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#0A1119] border-white/10 text-white">
                    {["Active", "Paused"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="bg-transparent border-white/12 text-white hover:bg-white/5 rounded-full">Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-[#4F8CFF] hover:bg-[#3f7af0] text-white rounded-full" data-testid="agent-form-save">{saving ? "Saving…" : editing ? "Save changes" : "Create agent"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="bg-[#0A1119] border-white/10 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete agent?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">This permanently removes "{toDelete?.name}" and cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-white/12 text-white hover:bg-white/5 rounded-full">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-500 hover:bg-red-600 text-white rounded-full" data-testid="agent-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
